import * as React from 'react';
import { Stack, Group, Text, Select, Button, NumberInput, Card, ActionIcon, Popover, ColorPicker, ColorSwatch, Divider } from '@mantine/core';
import { Plus, Trash } from 'lucide-react';
import { ColumnFormattingRule } from '@bi-studio/shared/lib';

interface ChartConditionalFormattingProps {
    value: ColumnFormattingRule[];
    onChange: (value: ColumnFormattingRule[]) => void;
}

export const ChartConditionalFormatting: React.FC<ChartConditionalFormattingProps> = ({ value, onChange }) => {
    const rules = value || [];

    const addRule = () => {
        const newRules = [...rules, {
            threshold: 0,
            operator: '>',
            backgroundColor: '#212529',
            textColor: '#ffffff'
        } as ColumnFormattingRule];
        onChange(newRules);
    };

    const updateRule = (index: number, updatedRule: Partial<ColumnFormattingRule>) => {
        const newRules = [...rules];
        newRules[index] = { ...newRules[index], ...updatedRule };
        onChange(newRules);
    };

    const removeRule = (index: number) => {
        const newRules = rules.filter((_, i) => i !== index);
        onChange(newRules.length > 0 ? newRules : []);
    };

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text size="sm" fw={500}>Conditional Rules</Text>
                <Button
                    variant="light"
                    size="xs"
                    leftSection={<Plus size={14} />}
                    onClick={addRule}
                >
                    Add Rule
                </Button>
            </Group>

            <Divider />

            {rules.length === 0 ? (
                <Text size="sm" c="dimmed" fs="italic" ta="center" py="sm">
                    No conditional formatting rules defined.
                </Text>
            ) : (
                <Stack gap="sm">
                    {rules.map((rule, idx) => (
                        <Card key={idx} withBorder p="xs" radius="sm">
                            <Stack gap="xs">
                                <Group align="flex-end" gap="xs">
                                    <Select
                                        label="If value"
                                        size="xs"
                                        style={{ flex: 1 }}
                                        value={rule.operator}
                                        onChange={(val) => updateRule(idx, { operator: val as any })}
                                        data={[
                                            { value: '>', label: '>' },
                                            { value: '>=', label: '>=' },
                                            { value: '<', label: '<' },
                                            { value: '<=', label: '<=' },
                                            { value: '==', label: '==' },
                                        ]}
                                    />
                                    <NumberInput
                                        label="Threshold"
                                        size="xs"
                                        style={{ flex: 1 }}
                                        value={rule.threshold}
                                        onChange={(val) => updateRule(idx, { threshold: typeof val === 'number' ? val : 0 })}
                                    />
                                    <Stack gap={4} w={60}>
                                        <Text size="xs" fw={500}>Color</Text>
                                        <Group h={30} align="center">
                                            <Popover position="bottom" withArrow shadow="md" trapFocus>
                                                <Popover.Target>
                                                    <ColorSwatch
                                                        color={rule.backgroundColor || '#212529'}
                                                        size={24}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </Popover.Target>
                                                <Popover.Dropdown p={8}>
                                                    <ColorPicker
                                                        value={rule.backgroundColor}
                                                        onChange={(val: string) => updateRule(idx, { backgroundColor: val })}
                                                        format="hex"
                                                    />
                                                </Popover.Dropdown>
                                            </Popover>
                                        </Group>
                                    </Stack>
                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        onClick={() => removeRule(idx)}
                                        mb={4}
                                    >
                                        <Trash size={16} />
                                    </ActionIcon>
                                </Group>
                            </Stack>
                        </Card>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};
